import { NS } from '@/constants';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { ATTRIBUTES } from '@/constants/product';
import { CDN_SERVER_URL, URLS } from '@/constants/urls';
import { FILTERS, FILTERS_TYPE, PRICE_FILTER_DEFAULT } from '@/constants/categ';
import { STORE_ID } from '@/constants/stores';

export const parseFilters = (filters, filterQuery, storeId) => {
  if (filters?.filters) {
    return filters?.filters?.map((filter) => {
      return {
        ...filter,
        nbByValue: getNbByValue(filter, filters.notes, storeId),
        displayable: filterIsDisplayable(getNbByValue(filter, filters.notes, storeId), filter, filterQuery)
      };
    });
  }
  return filters?.map((filter) => {
    return {
      ...filter,
      nbByValue: getNbByValue(filter, filters.notes, storeId),
      displayable: filterIsDisplayable(getNbByValue(filter, filters.notes, storeId), filter, filterQuery)
    };
  });
};

export const getNbByValue = (filter, notes, storeId) => {
  const { type, nbByValue, nameId } = filter;

  switch (type) {
    case FILTERS_TYPE.BLOC:

      if (nameId === 'millesime') {
        const filteredNbByValue = nbByValue.filter((year) => year.value !== '0');
        const oldDefaultYear = nbByValue.find((year) => year.value === '0');

        const frenchSpeakingCountries = [STORE_ID.FRANCE, STORE_ID.LUXEMBOURG, STORE_ID.BELGIQUE, STORE_ID.SUISSE];

        const value = frenchSpeakingCountries.includes(storeId) ? 'NM' : 'NV';

        const newDefaultYear = oldDefaultYear ? { ...oldDefaultYear, value } : null;

        return newDefaultYear ? [...filteredNbByValue, newDefaultYear] : filteredNbByValue;
      } else {
        return nbByValue;
      }

    default:
      return nbByValue;
  }
};

const filterIsDisplayable = (nbByValue, filter, filterQuery) => (
  (nbByValue?.length > 1) || (nbByValue?.length === 1) || filterQuery?.includes(filter.nameId)
);

export const isNote = (value) => (
  value?.match(/note_.*/)
);

export const getSelectedFiltersInfos = (t, filterString = '', allFilters, locale, currency) => {
  const filters = filterString.split('|').reduce((obj, item) => {
    const [key, value] = item.split(':');
    if (Object.keys(obj).includes(key)) {
      // Necessary because this function split the first string on the seconde iteration.
      return { ...obj, [key]: Array.isArray(obj[key]) ? [...obj[key], value] : [obj[key], value] };
    }
    return { ...obj, [key]: value };
  }, {});

  return Object.keys(filters).map((filter) => ({
    label: getTagLabel(t, filter, filters[filter], filters, allFilters, locale, currency),
    filterString: getFilterString(filter, filters),
    nameId: filter === FILTERS.MIN_PRICE ? FILTERS.PRICE : filter,
    valueId: filter === FILTERS.MIN_PRICE ? { min: filters[FILTERS.MIN_PRICE], max: filters[FILTERS.MAX_PRICE] } : filters[filter]
  })).filter((item) => item.label);
};

const getTagLabel = (t, nameId, valueId, filters, allFilters, locale, currency) => {
  let value = getValue(nameId, valueId, allFilters);
  let prices = getPriceKeys(filters);

  if (nameId === ATTRIBUTES.CONDITIONING && valueId === FILTERS.ONLY_CU) {
    value = t(`filter.${FILTERS.ONLY_CU}`, { ns: NS.CATEG });
  }

  if (value) {
    return nameId === FILTERS.PROMOTION ? `${getName(nameId, allFilters)} : ${value}` : value;
  }

  if (nameId === FILTERS.MIN_PRICE && prices.length === 2) {
    prices = prices.map((price) => filters[price]);
    return getPriceText(t, prices, locale, currency);
  }

  return '';
};

const getFilterString = (filter, filters) => {
  const prices = getPriceKeys(filters);
  if (filter === FILTERS.MIN_PRICE && prices.length === 2) {
    return prices.map((price) => `${price}:${filters[price]}`).join('|');
  }

  if (isNote(filter)) {
    return filters[filter].map((item) => `${filter}:${item}`).join('|');
  }

  return `${filter}:${filters[filter]}`;
};

const getValue = (nameId, valueId, allFilters) => (
  isNote(nameId) ? (
    allFilters?.find(
      (item) => item.nameId === 'critique'
    )?.nbByValue?.find(
      (item) => item.nameId === nameId
    )?.nbByValue?.find(
      (obj) => obj.valueId === valueId
    )?.value
  ) : (
    allFilters?.find((item) => item.nameId === nameId)?.nbByValue.find((obj) => obj.valueId === valueId)?.value
  )
);

const getName = (nameId, allFilters) => (
  allFilters?.find((item) => item.nameId === nameId)?.name
);

const getPriceKeys = (filters) => Object.keys(filters).filter((item) => item.includes(FILTERS.PRICE));

export const isFloatingPointNumber = (number) => number % 1 !== 0;

export const getPriceText = (t, values = [], locale, currency, addPlusSign = true) =>
  values.length > 0 && values.map((val, index, data) => (
    MillesimaPrice.format(t, {
      value: val,
      locale,
      currency,
      isInteger: true
    })
      // If there is multiple values, add "plus sign" only for the last element
      .concat((val >= PRICE_FILTER_DEFAULT.max && index === data.length - 1 && addPlusSign) ? '+' : '')
  )).join(' - ');

export const getValueId = (selectedValue, nbByValue) => nbByValue.find(({ value }) => value === selectedValue)?.valueId;

export const canBeSaved = (selection, val) => (
  isObjectEmpty(selection) ? !!val : true
);

export const getSelectedValue = (filter, tags) => {
  if (filter) {
    switch (filter.type) {
      case FILTERS_TYPE.PRICE:
        return tags.find((tag) => tag.nameId === FILTERS.PRICE)?.valueId;
      case FILTERS_TYPE.BLOC:
      case FILTERS_TYPE.CHECKBOX:
        const tagFilter = tags.find((tag) => tag.nameId === filter.nameId);
        if (tagFilter) {
          const { nameId, valueId } = tagFilter;
          const value = filter.nbByValue?.find((obj) => obj.valueId === valueId)?.value;
          if (value) {
            return { [`${nameId}-${valueId}`]: value };
          }
        }
        return {};
    }
  }
};

export const getPriceInfos = (selectedFiltersInfos) => {
  return getSelectedValue({ type: FILTERS_TYPE.PRICE }, selectedFiltersInfos);
};

export const parseSortOptions = (t, options) => options?.map((option) => ({
  ...option,
  label: t(`category:results.${option.label}`)
}));

export const deleteFilterString = (filterQuery, tag) => filterQuery
  .replace(tag.filterString, '') // delete filter
  .replace(/^(\|)|(\|)$/g, '') // delete '|' on start/end
  .replace(/(\|)\1+/g, '|'); // replace '||' by '|'

export const getCategImage = (image) => image ? `${CDN_SERVER_URL}/attachements/listing/${image}` : URLS.BACKGROUND_LISTING;

export const getCategImageWebp = (image) => image ?
  `${CDN_SERVER_URL}/attachements/webp/listing/${image.replace(/\.(jpg|jpeg|png)$/i, '.webp')}` :
  URLS.BACKGROUND_LISTING;
